import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import GlobalDelete from "../../components/common/GlobalDelete";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "STUDENT";

const Student = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    studentName: "",
    parentName: "",
    email: "",
    mobileNumber: "",
    month: "",
    status: "",
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const canRead = Boolean(storedScreens?.studentListingRead);
  const canCreate = Boolean(storedScreens?.studentListingCreate);
  const canUpdate = Boolean(storedScreens?.studentListingUpdate);
  const canDelete = Boolean(storedScreens?.studentListingDelete);
  const showActionColumn = canUpdate || canDelete;
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const defaultColumnVisibility = {
    studentChineseName: false,
    age: false,
    dateOfBirth: false,
    medicalCondition: false,
    assessedBy: false,
    assessmentCenter: false,
    assessmentDate: false,
    schoolType: false,
    schoolName: false,
    race: false,
    primaryLanguage: false,
    referByParent: false,
    referByStudent: false,
    remark: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        enableColumnOrdering: false,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "",
        enableHiding: true,
        enableSorting: false,
        size: 10,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "studentUniqueId",
        header: "Student ID",
        enableHiding: true,
        size: 40,
      },
      {
        accessorKey: "center",
        header: "Centre Name",
        enableHiding: true,
      },
      {
        accessorKey: "studentName",
        header: "Student Name",
        enableHiding: true,
      },
      {
        accessorKey: "parentPrimaryName",
        header: "Parent Name",
        enableHiding: true,
      },
      {
        accessorKey: "parentPrimaryEmail",
        header: "Parent Email",
        enableHiding: true,
      },
      {
        accessorKey: "parentPrimaryMobileNumber",
        header: "Parent Mobile",
        enableHiding: true,
      },
      {
        accessorKey: "gender",
        header: "Gender",
        enableHiding: true,
        size: 30,
      },
      {
        accessorKey: "allowMagazine",
        header: "Allow Magazine",
        enableHiding: true,
        size: 30,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        accessorKey: "allowSocialMedia",
        header: "Allow Social Media",
        enableHiding: true,
        size: 30,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        accessorKey: "age",
        header: "Age",
        enableHiding: true,
      },
      {
        accessorKey: "studentChineseName",
        header: "Chinese Name",
        enableHiding: true,
      },
      {
        accessorKey: "medicalCondition",
        header: "Medical Condition",
        enableHiding: true,
      },
      {
        accessorKey: "dateOfBirth",
        header: "Date of Birth",
        enableHiding: true,
      },
      {
        accessorKey: "schoolType",
        header: "School Type",
        enableHiding: true,
      },
      {
        accessorKey: "schoolName",
        header: "School Name",
        enableHiding: true,
      },
      {
        accessorKey: "race",
        header: "Race",
        enableHiding: true,
      },
      {
        accessorKey: "primaryLanguage",
        header: "Primary Language",
        enableHiding: true,
      },
      {
        accessorKey: "referByParent",
        header: "Referred By Parent",
        enableHiding: true,
      },
      {
        accessorKey: "assessedBy",
        header: "Assessed By",
        enableHiding: true,
      },
      {
        accessorKey: "assessmentCenter",
        header: "Assessment Center",
        enableHiding: true,
      },
      {
        accessorKey: "assessmentDate",
        header: "Assessment Date",
        enableHiding: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";

          return new Date(value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "referByStudent",
        header: "Referred By Student",
        enableHiding: true,
      },
      {
        accessorKey: "remark",
        header: "Remark",
        enableHiding: true,
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        enableHiding: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created On",
        enableHiding: true,
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        enableHiding: true,
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        enableHiding: true,
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,

    manualPagination: true, // ✅ IMPORTANT
    rowCount: rowCount,

    onPaginationChange: setPagination,
    state: {
      pagination,
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,

    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
    },

    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: () => navigate(`/student/view/${row.original.id}`),
          style: { cursor: "pointer" },
        })
      : undefined,

    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, data, "Student.csv")}
      >
        Export CSV
      </button>
    ),
  });

  const fetchStudentData = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams();
      filters.centerId = selectedCenter;

      for (let key in filters) {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      }

      // 👇 pagination params
      queryParams.append("page", pagination.pageIndex);
      queryParams.append("size", pagination.pageSize);

      const response = await api.get(
        `/getStudentWithCustomInfo?${queryParams.toString()}`,
      );

      // ✅ server pagination mapping
      setData(response.data.content);
      setRowCount(response.data.totalElements);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setIsClearFilterClicked(false);
    }
  };

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": { color: "#eb862a" },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#eb862a" },
          },
        },
      },
    },
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  useEffect(() => {
    fetchStudentData();
  }, [filters, selectedCenter, pagination.pageIndex, pagination.pageSize]);

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      studentName: "",
      parentName: "",
      email: "",
      mobileNumber: "",
      status: "",
    });
    setIsClearFilterClicked(true);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Student Management<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Student Listing
        </li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Student Listing
              </span>
            </span>
          </div>
        </div>
        <div className="mb-3">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 mb-1">
              <input type="hidden" name="centerId" value={filters.centerId} />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="studentName"
                value={filters.studentName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Student"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="parentName"
                value={filters.parentName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Parent"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Email"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="mobileNumber"
                value={filters.mobileNumber}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Mobile"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className="individual_fliters d-lg-flex mt-2">
              <div className="form-group mb-0 ms-2 mb-1">
                <input
                  type="month"
                  name="month"
                  value={filters.month}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm center_list"
                  placeholder="Month"
                  autoComplete="off"
                />
              </div>
              <div className="form-group mb-0 ms-2 mb-1">
                <select
                  className="form-select form-select-sm center_list"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option disabled value="">
                    Select Grade
                  </option>
                  <option value="END_CLASS">End Class</option>
                  <option value="SUSPEND">Suspend</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </select>
              </div>
              <div className="form-group mb-0 ms-2 mb-1 ">
                <button
                  type="button"
                  className="btn btn-sm btn-border"
                  onClick={clearFilter}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="mt-2 d-flex">
              {canCreate && (
                <Link to="/student/add">
                  <button type="button" className="btn btn-button btn-sm me-2">
                    Add <i className="bx bx-plus"></i>
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} enableColumnOrdering={false} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                disableScrollLock
              >
                {canUpdate && (
                  <MenuItem
                    onClick={() => navigate(`/student/edit/${selectedId}`)}
                    className="text-start mb-0 menuitem-style"
                  >
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteStudentDetail/${selectedId}`}
                      onDeleteSuccess={fetchStudentData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Student;
